import { ChevronRight } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { Avatar, Grid, IconButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { type GridColDef } from "@mui/x-data-grid-premium";
import React, { useContext } from "react";
import { useSearchParams } from "react-router-dom";
import CloseArrowTooltip from "../../../../components/CloseArrowTooltip";
import BaseTable from "../../../../components/datagrid/BaseTable.tsx";
import { TableToolbarButton } from "../../../../components/datagrid/TableToolbarButton";
import { ProjectContext } from "../../../../contexts/ProjectContext";

function stringAvatar(attributeType: PatientAttributeType) {
  if (attributeType.acronymText && attributeType.acronymText !== "") {
    return {
      children: `${attributeType.acronymText}`,
    };
  }

  const name = attributeType.name;
  let stringName;
  const allUpperCase = name.replace(/[a-z]/g, "").replace(/ /g, "");

  if (allUpperCase === "") {
    let acronym = "";
    const allWords = name.split(" ");
    allWords.forEach((word) => {
      if (word !== "") {
        acronym += word.trim()[0];
      }
    });
    stringName = acronym;
  } else {
    stringName = allUpperCase;
  }

  return {
    sx: {
      bgcolor: "rgba(0, 0, 0, 0.56)",
    },
    children: `${stringName}`,
  };
}

export default function CustomCategoriesListView() {
  const {
    id: projectId,
    attributeTypes,
    isLocked,
  } = useContext(ProjectContext)!;

  const [searchParams, setSearchParams] = useSearchParams();

  const customAttributeTypes = attributeTypes?.filter((attributeType) => {
    return attributeType.projectId === projectId;
  });

  const columns: GridColDef[] = [
    {
      field: "icon",
      headerName: "Icon",
      width: 120,
      renderCell: (params) => {
        const attributeType = params.row;
        return (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Avatar
              className="category-avatar"
              {...stringAvatar(attributeType)}
            />
          </Box>
        );
      },
      filterable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
    },
    {
      field: "attributeTypeFields",
      headerName: "Fields",
      width: 140,
      valueGetter: (value: PatientAttributeTypeField[]) => {
        return value
          .map((f) => `${f.label}${f.required ? "*" : ""}`)
          .join(", ");
      },
    },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 200,
      type: "boolean",
    },
    {
      field: "numApplied",
      headerName: "Applied",
      type: "number",
      width: 100,
      valueGetter: (_value, row) => {
        let usageCount = 0;
        const attributeTypeId = row.id;
        const attributeType = customAttributeTypes?.find(
          (pat) => pat.id === attributeTypeId,
        );
        if (attributeType) {
          usageCount = attributeType.usageCount;
        }
        return usageCount;
      },
      align: "center",
      headerAlign: "center",
    },
    {
      field: "details",
      headerName: "",
      width: 146,
      align: "right",
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => {
        const attributeTypeId = params.row.id;
        return (
          <IconButton
            onClick={() => {
              searchParams.set("mode", "detail");
              searchParams.set("attributeTypeId", attributeTypeId);
              setSearchParams(searchParams);
            }}
          >
            <ChevronRight />
          </IconButton>
        );
      },
    },
  ];

  const newCategoryButton = (
    <CloseArrowTooltip title={isLocked ? "This project is locked." : ""}>
      <span>
        <TableToolbarButton
          className="new-category-button"
          startIcon={<AddIcon />}
          onClick={() => {
            searchParams.set("mode", "create");
            setSearchParams(searchParams);
          }}
          disabled={isLocked}
        >
          New Category
        </TableToolbarButton>
      </span>
    </CloseArrowTooltip>
  );

  return (
    <Grid container className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Custom Categories</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            Create your own categories that can be applied to patients in this
            project.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        <Grid>
          <BaseTable
            className="custom-categories-table"
            columns={columns}
            rows={customAttributeTypes}
            slotProps={{
              toolbar: {
                showColumns: true,
                showFilter: true,
                customElements: newCategoryButton,
              },
            }}
            loading={false}
            tableId="custom-categories-table"
          />
        </Grid>
      </div>
    </Grid>
  );
}
